import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ChangeDetectorRef, inject, PLATFORM_ID } from '@angular/core';
import {
  AuthApiResponse,
  MemberAuth,
  persistMemberProfileFromEntity,
  readMemberProfileFromStorage,
} from '../../../services/member-auth';
import { Common } from '../../../services/common';
import { finalize } from 'rxjs';
import { MemberLedgerScreenAccessCustomerType } from '../../../shared/model/customerTypeConfig';

type SignInStep = 'memberCode' | 'otp';

/** OTP verify: backend may use 0 or 200 in the JSON body for success (HTTP is often always 200). */
function isVerifyOtpStatusOk(code: unknown): boolean {
  const n = typeof code === 'string' ? Number(code) : code;
  return n === 0 || n === 200;
}

function hasMemberPhone(response?: AuthApiResponse | null): boolean {
  return !!response?.entityResponse?.memberPhone?.trim();
}

const signInRequestOtpUi = {
  genericFallback: 'Member code is not valid.',
  mobileNotOnFile:
    'Your mobile number is not updated in the system. Kindly contact the KGA Admin team to have it updated.',
} as const;
// Maps known API outcomes to member-safe copy; otherwise uses API text or a generic fallback.
function messageAfterOtpRequest(apiMessage: string | null | undefined): string {
  const lower = (apiMessage ?? '').trim().toLowerCase();
  if (lower.includes('mobile number not found')) return signInRequestOtpUi.mobileNotOnFile;
  const trimmed = (apiMessage ?? '').trim();
  return trimmed || signInRequestOtpUi.genericFallback;
}

const RESEND_OTP_COOLDOWN_SECONDS = 30;
const MAX_OTP_RESEND_ATTEMPTS = 3;
const MAX_OTP_RESEND_MESSAGE = 'Maximum OTP resend attempts reached';

@Component({
  selector: 'app-sign-in',
  imports: [CommonModule, FormsModule],
  templateUrl: './sign-in.html',
  styleUrl: './sign-in.scss',
})
export class SignIn implements OnDestroy {
  step: SignInStep = 'memberCode';
  memberCode = '';
  otp = '';
  private otpRequestedFor = '';
  displayMemberLabel = 'Member';
  errorMessage = '';
  isSubmitting = false;
  isResendingOtp = false;
  resendAttemptCount = 0;
  resendLimitReached = false;
  resendCooldownSeconds = 0;
  private resendCooldownInterval?: ReturnType<typeof setInterval>;
  private readonly platformId = inject(PLATFORM_ID);
  private readonly cdr = inject(ChangeDetectorRef);

  constructor(
    private readonly router: Router,
    private readonly memberAuth: MemberAuth,
    private readonly common: Common,
  ) { }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    sessionStorage.removeItem('otpVerified');
    localStorage.removeItem('userToken');
    localStorage.removeItem('memberCode');
    sessionStorage.removeItem('otpVerifiedSession');
    localStorage.removeItem('policyGuestMode');
  }

  openPrivacyPolicy(): void {
    localStorage.setItem('policyGuestMode', 'true');
    void this.router.navigate(['/kga-website/privacy-policy']);
  }

  stringLowerNoSpace(value: any): string {
    if (!value) {
      return '';
    }

    return value
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  submitMemberCode(): void {
    if (this.isSubmitting) return;

    const cleanedCode = this.memberCode.trim();

    if (!cleanedCode) {
      this.errorMessage = 'Please enter member code.';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.memberAuth
      .requestOtp(cleanedCode)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response) => {

          const statusCode = response?.apiResponse?.statusCode;
          const apiMsg = response?.apiResponse?.message;

          if (statusCode !== 0 || !hasMemberPhone(response)) {
            const message = messageAfterOtpRequest(apiMsg);
            this.step = 'memberCode';
            this.errorMessage = message;
            this.common.showLightError(message);
            return;
          }

          const type = this.stringLowerNoSpace(response?.entityResponse?.customerTypeName);
          if (MemberLedgerScreenAccessCustomerType.includes(type)) {
            if (isPlatformBrowser(this.platformId)) {
              sessionStorage.setItem('otpVerified', 'false');
              persistMemberProfileFromEntity(response.entityResponse);
            }
            this.otpRequestedFor = cleanedCode;
            this.displayMemberLabel = cleanedCode;
            this.step = 'otp';
            this.otp = '';
            this.resendAttemptCount = 0;
            this.resendLimitReached = false;
            this.startResendCooldown(RESEND_OTP_COOLDOWN_SECONDS);
            this.common.showSuccess('OTP sent successfully to your registered mobile number.');
          } else {
            this.common.showLightError(
              `${response?.entityResponse?.customerTypeName} not allowd for the transaction`,
            );
          }
        },
        error: (error) => {
          const message = messageAfterOtpRequest(error?.error?.apiResponse?.message);
          this.errorMessage = message;
          this.common.showLightError(message);
        },
      });
  }

  submitOtp(): void {
    if (this.isSubmitting) return;

    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const storedMember = this.otpRequestedFor || readMemberProfileFromStorage().memberCode || '';
    const sanitizedOtp = this.otp.replace(/\D/g, '').slice(0, 6);

    if (sanitizedOtp.length !== 6) {
      this.errorMessage = 'Please enter a valid 6-digit OTP.';
      return;
    }

    if (!storedMember) {
      this.errorMessage = 'Member code not found. Please sign in again.';
      this.step = 'memberCode';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.memberAuth
      .verifyOtp(storedMember, sanitizedOtp)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (response) => {
          const statusCode = response?.apiResponse?.statusCode;
          const message = response?.apiResponse?.message ?? '';
          const entity = response?.entityResponse;
          const sessionToken = entity?.sesssionToken ?? entity?.sessionToken;

          if (!isVerifyOtpStatusOk(statusCode)) {
            const msg = message.trim() || 'OTP verification failed.';
            this.errorMessage = msg;
            this.common.showError(msg);
            return;
          }

          // Same HTTP status can still mean failure: e.g. statusCode 200 + "No OTP request found." + no token.
          if (!sessionToken?.trim()) {
            this.errorMessage = message || 'OTP verification failed.';
            this.common.showLightError(this.errorMessage);
            return;
          }

          sessionStorage.setItem('otpVerified', 'true');
          sessionStorage.setItem('otpVerifiedSession', 'true');
          localStorage.setItem('userToken', sessionToken);
          localStorage.removeItem('policyGuestMode');

          persistMemberProfileFromEntity(entity, { clearPhotoIfMissing: true });
          if (entity?.customerSubTypeid) localStorage.setItem('moduleId', entity.customerSubTypeid);
          this.errorMessage = '';

          this.common.showSuccess(message || 'Login successful.');
          void this.router.navigate(['/member_ledger/master'], { state: { profile: entity } });
        },
        error: (error) => {
          const msg =
            error?.error?.apiResponse?.message?.trim() || 'OTP verification failed.';
          this.errorMessage = msg;
          this.common.showError(msg);
        },
      });
  }

  resendOtp(): void {
    if (this.resendLimitReached) {
      this.common.showWarning(MAX_OTP_RESEND_MESSAGE);
      return;
    }

    if (this.isSubmitting || this.isResendingOtp || this.resendCooldownSeconds > 0) {
      return;
    }

    const memberCode = (this.otpRequestedFor || readMemberProfileFromStorage().memberCode || '').trim();
    if (!memberCode) {
      this.errorMessage = 'Member code not found. Please sign in again.';
      this.step = 'memberCode';
      return;
    }

    this.isResendingOtp = true;
    this.errorMessage = '';
    this.memberAuth
      .requestOtp(memberCode)
      .pipe(finalize(() => (this.isResendingOtp = false)))
      .subscribe({
        next: (response) => {
          const statusCode = response?.apiResponse?.statusCode;
          const apiMsg = response?.apiResponse?.message;

          if (statusCode !== 0 || !hasMemberPhone(response)) {
            const message = messageAfterOtpRequest(apiMsg);
            this.errorMessage = message;
            this.common.showLightError(message);
            return;
          }

          this.otp = '';
          this.resendAttemptCount += 1;

          if (this.resendAttemptCount >= MAX_OTP_RESEND_ATTEMPTS) {
            this.resendLimitReached = true;
            this.clearResendCooldown();
            this.common.showWarning(MAX_OTP_RESEND_MESSAGE);
            return;
          }

          this.startResendCooldown(RESEND_OTP_COOLDOWN_SECONDS);
          this.common.showSuccess(response?.apiResponse?.message || 'OTP resent successfully.');
        },
        error: (error) => {
          const message = messageAfterOtpRequest(error?.error?.apiResponse?.message);
          this.errorMessage = message;
          this.common.showLightError(message);
        },
      });
  }

  backToMemberCode(): void {
    this.step = 'memberCode';
    this.otp = '';
    this.otpRequestedFor = '';
    this.errorMessage = '';
    this.resendAttemptCount = 0;
    this.resendLimitReached = false;
    this.clearResendCooldown();
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem('memberCode');
      sessionStorage.removeItem('otpVerified');
    }
  }

  ngOnDestroy(): void {
    this.clearResendCooldown();
  }

  private startResendCooldown(seconds = RESEND_OTP_COOLDOWN_SECONDS): void {
    this.clearResendCooldown();
    this.resendCooldownSeconds = seconds;
    this.resendCooldownInterval = setInterval(() => {
      this.resendCooldownSeconds -= 1;
      if (this.resendCooldownSeconds <= 0) {
        this.clearResendCooldown();
      }
      this.cdr.markForCheck();
    }, 1000);
  }

  private clearResendCooldown(): void {
    if (this.resendCooldownInterval) {
      clearInterval(this.resendCooldownInterval);
      this.resendCooldownInterval = undefined;
    }
    this.resendCooldownSeconds = 0;
  }
}
